import React, { useState, useEffect } from 'react';
import imageCompression from 'browser-image-compression';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import axios from '../api';
import Snackbar from '../components/Snackbar';

const genders = [
  { value: 'male', label: 'مرد' },
  { value: 'female', label: 'زن' },
  { value: 'other', label: 'سایر' }
];

const UserProfile = () => {
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    profile_picture: null,
    gender: '',
    age: '',
    address: ''
  });

  useEffect(() => {
    document.title = 'پروفایل کاربری | آگاه‌جو';
  }, []);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios.get('/profile/').then(res => {
      setProfile({
        first_name: res.data.first_name || '',
        last_name: res.data.last_name || '',
        phone: res.data.phone || '',
        profile_picture: null,
        gender: res.data.gender || '',
        age: res.data.age !== null && res.data.age !== undefined ? res.data.age : '',
        address: res.data.address || ''
      });
      // If profile_picture is a URL or path, set it for preview
      if (res.data.profile_picture) {
        // If backend returns a relative path, prepend baseURL
        const isAbsolute = res.data.profile_picture.startsWith('http');
        setAvatarPreview(isAbsolute ? res.data.profile_picture : `${axios.defaults.baseURL.replace(/\/api$/, '')}${res.data.profile_picture}`);
      } else {
        setAvatarPreview('');
      }
    });
  }, []);

  const handleChange = async e => {
    const { name, value, files } = e.target;
    if (name === 'profile_picture' && files && files[0]) {
      try {
        const options = { maxSizeMB: 1, useWebWorker: true };
        const compressedFile = await imageCompression(files[0], options);
        setProfile(prev => ({ ...prev, profile_picture: compressedFile }));
        setAvatarPreview(URL.createObjectURL(compressedFile));
      } catch (err) {
        setProfile(prev => ({ ...prev, profile_picture: files[0] }));
        setAvatarPreview(URL.createObjectURL(files[0]));
      }
    } else {
      setProfile(prev => ({ ...prev, [name]: value ?? '' }));
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    const formData = new FormData();
    Object.keys(profile).forEach(key => {
      // Only append file if it's a File object
      if (key === 'profile_picture' && profile.profile_picture instanceof File) {
        formData.append(key, profile.profile_picture);
      } else {
        formData.append(key, profile[key] ?? '');
      }
    });
    axios.put('/profile/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => {
      setProfile({
        first_name: res.data.first_name || '',
        last_name: res.data.last_name || '',
        phone: res.data.phone || '',
        profile_picture: null,
        gender: res.data.gender || '',
        age: res.data.age !== null && res.data.age !== undefined ? res.data.age : '',
        address: res.data.address || ''
      });
      // Reload profile picture if exists
      if (res.data.profile_picture) {
        const isAbsolute = res.data.profile_picture.startsWith('http');
        setAvatarPreview(isAbsolute ? res.data.profile_picture : `${axios.defaults.baseURL.replace(/\/api$/, '')}${res.data.profile_picture}`);
      } else {
        setAvatarPreview('');
      }
  setError('');
  setSuccess(true);
    }).catch(err => {
      if (err.response) {
        if (err.response.status === 404) {
          setError('پروفایل یافت نشد.');
        } else if (err.response.status === 401) {
          setError('دسترسی غیرمجاز. لطفاً دوباره وارد شوید.');
        } else if (err.response.data && err.response.data.error) {
          setError(err.response.data.error);
        } else {
          setError('خطا در ذخیره پروفایل. لطفاً بعداً تلاش کنید.');
        }
      } else {
  setError('ارتباط با سرور برقرار نشد.');
      }
  setSuccess(false);
    });
  };

  return (
    <Box sx={{ direction: 'rtl', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', px: 2, py: 8 }}>
      <Paper elevation={4} sx={{ borderRadius: 4, p: 4, width: '100%', maxWidth: 500, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="h4" fontWeight={800} color="primary" align="right" gutterBottom>
          پروفایل کاربری
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar src={avatarPreview} sx={{ width: 72, height: 72 }} />
          <Button variant="contained" component="label" color="primary" sx={{ fontWeight: 700 }}>
            انتخاب تصویر
            <input type="file" name="profile_picture" hidden onChange={handleChange} accept="image/*" />
          </Button>
        </Box>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Snackbar open={success} onClose={() => setSuccess(false)} message="تغییرات با موفقیت ذخیره شد" severity="success" />
          <TextField name="first_name" value={profile.first_name ?? ''} onChange={handleChange} label="نام" variant="outlined" fullWidth inputProps={{ style: { textAlign: 'right' } }} />
          <TextField name="last_name" value={profile.last_name ?? ''} onChange={handleChange} label="نام خانوادگی" variant="outlined" fullWidth inputProps={{ style: { textAlign: 'right' } }} />
          <TextField name="phone" value={profile.phone ?? ''} label="شماره تلفن" variant="outlined" fullWidth inputProps={{ style: { textAlign: 'right' } }} disabled />
          <TextField name="gender" value={profile.gender ?? ''} onChange={handleChange} label="جنسیت" variant="outlined" select fullWidth inputProps={{ style: { textAlign: 'right' } }}>
            {genders.map(option => (
              <MenuItem key={option.value} value={option.value} sx={{ fontFamily: 'Vazirmatn' }}>{option.label}</MenuItem>
            ))}
          </TextField>
          <TextField name="age" value={profile.age ?? ''} onChange={handleChange} label="سن" variant="outlined" type="number" fullWidth inputProps={{ style: { textAlign: 'right' } }} />
          <TextField name="address" value={profile.address ?? ''} onChange={handleChange} label="آدرس" variant="outlined" fullWidth inputProps={{ style: { textAlign: 'right' } }} />
          {error && <Typography color="error" align="right">{error}</Typography>}
          <Button type="submit" variant="contained" color="success" size="large" sx={{ borderRadius: 2, fontWeight: 700 }}>
            ذخیره تغییرات
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default UserProfile;
